import { Link } from 'react-router-dom'
import { useServices } from '@/lib/cluster'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { StatusBadge } from '@/components/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useHealthChecks, type HealthCheck } from '@/lib/health-checks'

function serviceDot(checks: HealthCheck[] | undefined): { color: string; label: string } {
  if (!checks || checks.length === 0) return { color: 'bg-transparent', label: '' }
  let anyDown = false
  let anyAuto = false
  let anyActive = false
  for (const c of checks) {
    if (c.auto_disabled_at) { anyAuto = true; continue }
    if (!c.enabled) continue
    anyActive = true
    if (
      c.last_status == null ||
      c.last_status < c.expect_min ||
      c.last_status > c.expect_max
    ) {
      anyDown = true
    }
  }
  if (anyAuto) return { color: 'bg-amber-500', label: 'auto-disabled checks' }
  if (anyDown) return { color: 'bg-destructive', label: 'check failing' }
  if (anyActive) return { color: 'bg-emerald-500', label: 'all checks up' }
  return { color: 'bg-transparent', label: '' }
}

export function ServicesPage() {
  const { data, isLoading, error } = useServices()
  const { data: allChecks } = useHealthChecks()
  const byService = new Map<string, HealthCheck[]>()
  for (const c of allChecks ?? []) {
    const arr = byService.get(c.service_id) ?? []
    arr.push(c)
    byService.set(c.service_id, arr)
  }
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Services</h1>
        <p className="text-muted-foreground">All Swarm services known to Zeus.</p>
      </div>

      {error && <p className="text-sm text-destructive">Failed to load services.</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading &&
          Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        {data?.map((s) => {
          const stack = s.labels['com.docker.stack.namespace']
          const type = s.classification_override ?? s.classification_auto ?? 'unknown'
          const dot = serviceDot(byService.get(s.id))
          return (
            <Link key={s.id} to={`/services/${s.id}`} className="block">
              <Card className="h-full hover:border-primary/50 transition cursor-pointer">
                <CardHeader className="flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">{s.name}</CardTitle>
                  <div className="flex items-center gap-2">
                    {dot.color !== 'bg-transparent' && (
                      <span
                        title={dot.label}
                        className={`inline-block h-2 w-2 rounded-full ${dot.color}`}
                      />
                    )}
                    <StatusBadge status={s.status} />
                  </div>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground space-y-2">
                  <div className="truncate" title={s.image ?? ''}>{s.image ?? '—'}</div>
                  <div className="flex gap-1">
                    <Badge variant="outline">{type}</Badge>
                    {stack && <Badge variant="secondary">{stack}</Badge>}
                  </div>
                </CardContent>
              </Card>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
